import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ClipboardList } from 'lucide-react';
import { jsonApi } from '@/lib/jsonApi';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ClassificationPill } from '@/components/taxonomy/FactorRow';

function SurveyRow({ survey, groups, canEdit }) {
  const [mode, setMode] = useState(survey.mode);
  const [picked, setPicked] = useState(new Set(survey.factor_ids));
  const [saved, setSaved] = useState({ mode: survey.mode, ids: survey.factor_ids });
  const [busy, setBusy] = useState(false);
  const dirty = mode !== saved.mode || (mode === 'selected' && [...picked].sort().join() !== [...saved.ids].sort().join());
  const toggle = (fid) => setPicked((s) => { const n = new Set(s); if (n.has(fid)) n.delete(fid); else n.add(fid); return n; });
  const save = async () => {
    setBusy(true);
    try {
      const d = await jsonApi(`/taxonomy/surveys/${survey.survey_id}`, 'PUT', { mode, factor_ids: mode === 'selected' ? [...picked] : [] });
      setSaved({ mode: d.mode, ids: d.factor_ids });
      toast.success('Survey factors saved');
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-2xl p-5" data-testid={`survey-applicability-${survey.survey_id}`}>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-[#0F172A] truncate">{survey.name}</p>
          <p className="text-xs text-[#64748B] capitalize">{survey.status || 'draft'}</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={mode} onValueChange={setMode} disabled={!canEdit}>
            <SelectTrigger className="w-[230px] text-sm" data-testid={`survey-mode-${survey.survey_id}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All active factors</SelectItem>
              <SelectItem value="selected">Only selected factors</SelectItem>
            </SelectContent>
          </Select>
          {canEdit && <Button size="sm" disabled={!dirty || busy} onClick={save} className="bg-[#1A3E6F] hover:bg-[#15325A]" data-testid={`survey-save-${survey.survey_id}`}>{busy ? 'Saving…' : 'Save'}</Button>}
        </div>
      </div>
      {mode === 'selected' && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-5 pt-4 border-t border-[#F1F5F9]">
          {groups.map((g) => (
            <div key={g.attribute_id}>
              <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-[#64748B] mb-2">{g.name}</p>
              {g.factors.map((f) => (
                <label key={f.factor_id} className="flex items-center justify-between gap-2 py-1.5 text-sm text-[#0F172A] cursor-pointer">
                  <span className="flex items-center gap-2"><Checkbox checked={picked.has(f.factor_id)} disabled={!canEdit} onCheckedChange={() => toggle(f.factor_id)} data-testid={`survey-factor-${survey.survey_id}-${f.factor_id}`} />{f.name}</span>
                  <ClassificationPill value={f.classification} />
                </label>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SurveyApplicability({ attributes, canEdit }) {
  const [surveys, setSurveys] = useState(null);
  useEffect(() => { jsonApi('/taxonomy/surveys').then((d) => setSurveys(d.surveys)).catch((e) => { toast.error(e.message); setSurveys([]); }); }, []);
  const groups = useMemo(() => attributes.filter((a) => a.status === 'active')
    .map((a) => ({ ...a, factors: a.factors.filter((f) => f.status === 'active') })).filter((a) => a.factors.length), [attributes]);
  if (!surveys) return <p className="text-sm text-[#94A3B8] py-8 text-center">Loading surveys…</p>;
  return (
    <div className="space-y-4" data-testid="survey-applicability">
      <p className="text-sm text-[#64748B] max-w-3xl">Choose which factors AI and your team can tag on each survey. By default every survey uses all active factors.</p>
      {!surveys.length && <div className="text-center py-12 text-[#94A3B8]"><ClipboardList className="w-8 h-8 mx-auto mb-2" />No surveys yet.</div>}
      {surveys.map((s) => <SurveyRow key={s.survey_id} survey={s} groups={groups} canEdit={canEdit} />)}
    </div>
  );
}
